import { Button, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { RentalTrailer } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";

import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import { RentalTrailerModal } from "./rental-trailer-modal";


type RentalTrailersProps = {
  rentalTrailers?: RentalTrailer[],
  onRentalTrailerSelected: (a: RentalTrailer) => void
}

const RentalTrailers = memo(({ rentalTrailers, onRentalTrailerSelected }: RentalTrailersProps) => {
  const { sortedList, sortData } = useSort(rentalTrailers, "trailerUnitNumber");

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["trailerUnitNumber","make","model","year","vin","licensePlateState","licensePlate"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Trailers" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="trailerUnitNumber" sortData={sortData} >
                Unit Number
              </SortableHeader>

              <SortableHeader sortKey="make" sortData={sortData} >
                Make
              </SortableHeader>

              <SortableHeader sortKey="model" sortData={sortData} >
                Model
              </SortableHeader>

              <SortableHeader sortKey="year" sortData={sortData} >
                Year
              </SortableHeader>

              <SortableHeader sortKey="vin" sortData={sortData} >
                VIN
              </SortableHeader>

              <SortableHeader sortKey="licensePlate" sortData={sortData} >
                Plate Number
              </SortableHeader>

              <SortableHeader sortKey="licensePlateState" sortData={sortData} >
                Plate State
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((rentalTrailer, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onRentalTrailerSelected(rentalTrailer)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {rentalTrailer.trailerUnitNumber}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.make}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.model}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.year}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.vin}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.licensePlate}
              </TruxTableCell>
              <TruxTableCell>
                {rentalTrailer.licensePlateState}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  rentalTrailers?: RentalTrailer[],
}

export default function Page() {
  const settings = useSettingsContext();
  const router = useRouter();
  const dataLoader = useCallback(() => ({
    rentalTrailers: axios.get(endpoints.rentalTrailers.findAllInactive).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const [isOpen, setIsOpen] = useState(false);

  const selectedRentalTrailerModel = useModelSavingData<RentalTrailer>(
    endpoints.rentalTrailers.update,
    endpoints.rentalTrailers.create
  );

  const updateRentalTrailer = () => {
    selectedRentalTrailerModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the Trailer");
        const rentalTrailer = response.data as RentalTrailer;
        if (rentalTrailer.active) {
          updateData("rentalTrailers")(deleteById(data!.rentalTrailers!, rentalTrailer))
        } else {
          updateData("rentalTrailers")(replaceById(data!.rentalTrailers!, rentalTrailer));
        }

        setIsOpen(false);
      }
    })
  }

  const onRentalTrailerSelected = useCallback((a: RentalTrailer) => {
    selectedRentalTrailerModel.set({
      ...a
    });
    setIsOpen(true);
  }, [selectedRentalTrailerModel, setIsOpen]);


  const rentalTrailersButton = <Button
    onClick={() => router.push("/app/rentals/trailers/list")}
    variant="soft"
  >
    Active Trailers
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.rentalTrailers?.length !== 0 &&
          <PageHeader title="Inactive Trailers">
            {rentalTrailersButton}
          </PageHeader>
        }

        {data && data?.rentalTrailers?.length === 0 &&
          <NoneFound title="No Inactive Trailers Found" message="To go to the Trailers page click the button below.">
            {rentalTrailersButton}
          </NoneFound>
        }

        {data && data?.rentalTrailers?.length !== 0 && <RentalTrailers rentalTrailers={data.rentalTrailers} onRentalTrailerSelected={onRentalTrailerSelected} />}

        {data && <RentalTrailerModal
          selectedRentalTrailerModel={selectedRentalTrailerModel}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          updateRentalTrailer={updateRentalTrailer}
        />}
      </DisplayPageData>
    </Container >
  );
}
